import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import type { RootState } from "../../store";
import type { AuthState } from "../../../types/states/auth.state.type";
import type { LoginDataResponse } from "../../../types/response/auth.response.type";
import { CryptoHelper } from "../../../utils/helpers/crypto.helper";

const storedUser = localStorage.getItem("user");
const storedStore = localStorage.getItem("store");
const storedToken = localStorage.getItem("token");
const storedEvents = localStorage.getItem("events");
const storedMenu = localStorage.getItem("menu");
const storedRoles = localStorage.getItem("roles");
const storedBrand = localStorage.getItem("brand");

const initialState: AuthState = {
  user: storedUser ? CryptoHelper.decryptArray(storedUser) : null,
  store: storedStore ? CryptoHelper.decryptArray(storedStore) : null,
  token: storedToken ? CryptoHelper.decrypt(storedToken) : null,
  events: storedEvents ? CryptoHelper.decryptArray(storedEvents) : null,
  menu: storedMenu ? CryptoHelper.decryptArray(storedMenu) : null,
  roles: storedRoles ? CryptoHelper.decryptArray(storedRoles) : null,
  brand: storedBrand ? CryptoHelper.decryptArray(storedBrand) : null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setCredentials: (state, { payload }: PayloadAction<LoginDataResponse>) => {
      const { user, session, events, menu, store, roles, brand } = payload;
      const encryptedUser = CryptoHelper.encrypt(JSON.stringify(user));
      const encryptedStore = CryptoHelper.encrypt(JSON.stringify(store));
      const encryptedToken = CryptoHelper.encrypt(session.token);
      const encryptedEvents = CryptoHelper.encrypt(JSON.stringify(events));
      const encryptedMenu = CryptoHelper.encrypt(JSON.stringify(menu));
      const encryptedRoles = CryptoHelper.encrypt(JSON.stringify(roles));
      const encryptedBrand = CryptoHelper.encrypt(JSON.stringify(brand));

      state.user = user;
      state.store = store;
      state.token = session.token;
      state.events = events;
      state.menu = menu;
      state.roles = roles;
      state.brand = brand;

      localStorage.setItem("token", encryptedToken);
      localStorage.setItem("user", encryptedUser);
      localStorage.setItem("store", encryptedStore);
      localStorage.setItem("events", encryptedEvents);
      localStorage.setItem("menu", encryptedMenu);
      localStorage.setItem("roles", encryptedRoles);
      localStorage.setItem("brand", encryptedBrand);
    },
    clearCredentials: (state) => {
      state.user = null;
      state.store = null;
      state.token = null;
      state.events = null;
      state.menu = null;
      state.roles = null;
      state.brand = null;
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("store");
      localStorage.removeItem("events");
      localStorage.removeItem("menu");
      localStorage.removeItem("last-menu");
      localStorage.removeItem("roles");
      localStorage.removeItem("brand");
    },
  },
});

export const authSelector = (state: { auth: AuthState }) => state.auth;
export const selectCurrentUser = (state: RootState) => state.auth.user;
export const selectUserEvents = (state: RootState) => state.auth.events;

export const { setCredentials, clearCredentials } = authSlice.actions;
export default authSlice.reducer;
